import React from 'react';

/* ── Coverage bar chart ──────────────────────────────────────────────────── */
function CoverageChart({ data }) {
  if (!data.length) return null;
  return (
    <div className="chart-card">
      <div className="chart-title">🧪 Test Coverage</div>
      <div className="bar-chart">
        {data.map(d => {
          const pct   = Math.min(Math.round(d.coverage), 100);
          const cls   = pct >= 80 ? 'good' : pct >= 60 ? 'medium' : 'poor';
          return (
            <div key={d.name} className="bar-row">
              <div className="bar-label" title={d.name}>{d.name.replace('legacy-', '')}</div>
              <div className="bar-track">
                <div className={`bar-fill ${cls}`} style={{ width: `${pct}%` }}>
                  {pct > 15 && <span className="bar-pct">{pct}%</span>}
                </div>
              </div>
              {pct <= 15 && (
                <span style={{ fontSize: 11, color: 'var(--txt2)', marginLeft: 4 }}>{pct}%</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Security donut ──────────────────────────────────────────────────────── */
const SEV_COLOR = { HIGH: 'var(--red)', MEDIUM: 'var(--yellow)', LOW: 'var(--green)' };
const SEV_BG    = { HIGH: 'var(--red-lt)', MEDIUM: 'var(--yellow-lt)', LOW: 'var(--green-lt)' };

// Map common finding patterns to actionable fix suggestions
function getFix(finding) {
  const f = finding.toLowerCase();
  if (f.includes('md5') || f.includes('sha1'))
    return 'Replace with hashlib.pbkdf2_hmac or bcrypt for passwords, hashlib.sha256 for checksums.';
  if (f.includes('eval(') || f.includes('exec('))
    return 'Remove eval/exec entirely. Use explicit logic or ast.literal_eval for safe parsing.';
  if (f.includes('shell=true'))
    return 'Pass command as a list instead: subprocess.run(["cmd", "arg"]) — never shell=True with user input.';
  if (f.includes('pickle'))
    return 'Use json.loads/json.dumps instead. Pickle executes arbitrary code on deserialization.';
  if (f.includes('non-cryptographic random') || f.includes('random.'))
    return 'Use secrets.token_hex() or secrets.token_urlsafe() for security-sensitive values.';
  if (f.includes('debug mode'))
    return 'Set DEBUG=False in production. Use environment variable: app.config["DEBUG"] = os.environ.get("DEBUG","false").lower() == "true"';
  if (f.includes('ssl') || f.includes('verify=false'))
    return 'Never disable SSL verification. Remove verify=False and ensure valid certificates are in place.';
  if (f.includes('hardcoded password') || f.includes('hardcoded secret'))
    return 'Move to environment variables: os.environ.get("SECRET_KEY"). Use a secrets manager in production.';
  if (f.includes('print('))
    return 'Replace print() with logging.getLogger(__name__).info(). Configure log level via environment.';
  if (f.includes('broad exception') || f.includes('except:'))
    return 'Catch specific exceptions: except ValueError as e. Log the error before handling or re-raising.';
  if (f.includes('todo') || f.includes('fixme') || f.includes('hack'))
    return 'Resolve before deploying to production. Create a tracked issue if deferring.';
  return 'Review this finding and apply the principle of least privilege / secure defaults.';
}

function FindingRow({ finding }) {
  const [open, setOpen] = React.useState(false);
  // Parse severity from "[HIGH] message" or "Severity: High — message"
  const sevMatch = finding.match(/^\[?(HIGH|MEDIUM|LOW)\]?\s*/i);
  const sev      = sevMatch ? sevMatch[1].toUpperCase() : 'LOW';
  const msg      = finding.replace(/^\[?(HIGH|MEDIUM|LOW)\]?\s*/i, '').trim();
  const fix      = getFix(finding);

  return (
    <div style={{ borderBottom: '1px solid #EFF2F5' }}>
      <div onClick={() => setOpen(o => !o)} style={{
        display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 0',
        cursor: 'pointer', userSelect: 'none',
      }}>
        <span style={{
          fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, flexShrink: 0,
          background: SEV_BG[sev], color: SEV_COLOR[sev], marginTop: 1,
        }}>{sev}</span>
        <span style={{ fontSize: 11, color: 'var(--txt)', flex: 1, lineHeight: 1.4 }}>{msg}</span>
        <span style={{ fontSize: 10, color: 'var(--txt2)', flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{
          margin: '0 0 8px 0', padding: '8px 10px', borderRadius: 6,
          background: '#F6F8FA', border: '1px solid var(--border)',
          fontSize: 11, color: 'var(--txt)', lineHeight: 1.6,
        }}>
          <span style={{ fontWeight: 700, color: '#1A7F37' }}>How to fix: </span>{fix}
        </div>
      )}
    </div>
  );
}

function AppSecurityRow({ d }) {
  const [open, setOpen] = React.useState(false);
  const hasFindings = d.findings?.length > 0;
  const total = (d.high || 0) + (d.medium || 0) + (d.low || 0);

  return (
    <div style={{ borderBottom: '1px solid #EFF2F5' }}>
      <div onClick={() => hasFindings && setOpen(o => !o)} style={{
        display: 'flex', alignItems: 'center', gap: 6, fontSize: 11,
        padding: '5px 0', cursor: hasFindings ? 'pointer' : 'default',
      }}>
        <span style={{ color: 'var(--txt2)', width: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {d.name.replace('legacy-', '')}
        </span>
        {d.high   > 0 && <span style={{ background: 'var(--red-lt)',    color: 'var(--red)',    padding: '1px 6px', borderRadius: 8, fontWeight: 700 }}>{d.high}H</span>}
        {d.medium > 0 && <span style={{ background: 'var(--yellow-lt)', color: 'var(--yellow)', padding: '1px 6px', borderRadius: 8, fontWeight: 700 }}>{d.medium}M</span>}
        {d.low    > 0 && <span style={{ background: 'var(--green-lt)',  color: 'var(--green)',  padding: '1px 6px', borderRadius: 8, fontWeight: 700 }}>{d.low}L</span>}
        {total === 0 && <span style={{ color: 'var(--green)', fontSize: 10 }}>✓ clean</span>}
        {hasFindings && (
          <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--txt2)' }}>{open ? '▲' : '▼'}</span>
        )}
      </div>
      {open && hasFindings && (
        <div style={{ paddingLeft: 8, paddingBottom: 4 }}>
          {d.findings.map((f, i) => <FindingRow key={i} finding={f} />)}
        </div>
      )}
    </div>
  );
}

function SecurityDonut({ data }) {
  if (!data.length) return null;

  const totals = data.reduce((acc, d) => ({
    high:   acc.high   + (d.high   || 0),
    medium: acc.medium + (d.medium || 0),
    low:    acc.low    + (d.low    || 0),
  }), { high: 0, medium: 0, low: 0 });

  const total = totals.high + totals.medium + totals.low || 1;

  const r = 36, cx = 44, cy = 44, stroke = 14;
  const circ = 2 * Math.PI * r;

  const segments = [
    { label: 'HIGH',   count: totals.high,   color: 'var(--red)',    pct: totals.high   / total },
    { label: 'MEDIUM', count: totals.medium, color: 'var(--yellow)', pct: totals.medium / total },
    { label: 'LOW',    count: totals.low,    color: 'var(--green)',  pct: totals.low    / total },
  ];

  let offset = 0;
  const arcs = segments.map(seg => {
    const dash = seg.pct * circ;
    const gap  = circ - dash;
    const arc  = { ...seg, dash, gap, offset };
    offset += dash;
    return arc;
  });

  return (
    <div className="chart-card">
      <div className="chart-title">🔒 Security Findings</div>
      <div className="donut-wrap">
        <svg width="88" height="88" className="donut-svg">
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--bg3)" strokeWidth={stroke} />
          {arcs.map((arc, i) => arc.dash > 0 && (
            <circle key={i} cx={cx} cy={cy} r={r} fill="none"
              stroke={arc.color} strokeWidth={stroke}
              strokeDasharray={`${arc.dash} ${arc.gap}`}
              strokeDashoffset={-arc.offset}
              style={{ transform: 'rotate(-90deg)', transformOrigin: `${cx}px ${cy}px` }}
            />
          ))}
          <text x={cx} y={cy + 5} textAnchor="middle" fontSize="14" fontWeight="700" fill="var(--txt)">
            {totals.high + totals.medium + totals.low}
          </text>
        </svg>
        <div className="donut-legend">
          {segments.map(seg => (
            <div key={seg.label} className="donut-legend-item">
              <div className="donut-dot" style={{ background: seg.color }} />
              <span style={{ color: 'var(--txt2)', fontSize: 12 }}>{seg.label}</span>
              <span style={{ fontWeight: 700, fontSize: 12, marginLeft: 4 }}>{seg.count}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Per-app breakdown — expandable to show findings + fixes */}
      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--txt2)', textTransform: 'uppercase',
          letterSpacing: '0.5px', marginBottom: 6 }}>
          Click an app to see findings &amp; fixes
        </div>
        {data.map(d => <AppSecurityRow key={d.name} d={d} />)}
      </div>
    </div>
  );
}

/* ── Modernization score chart ───────────────────────────────────────────── */
function ScoreChart({ data }) {
  if (!data.length) return null;

  const REC_COLOR = {
    Retire:   'var(--red)',
    Refactor: 'var(--yellow)',
    New:      'var(--green)',
  };

  return (
    <div className="chart-card">
      <div className="chart-title">🧠 AI Modernization Assessment</div>
      <div className="score-list">
        {data.map(d => {
          const filled = Math.round(d.score);
          const color  = REC_COLOR[d.rec] || 'var(--blue)';
          return (
            <div key={d.name} className="score-row">
              <div className="score-name" title={d.name}>{d.name.replace('legacy-', '')}</div>
              <div className="score-dots">
                {Array.from({ length: 10 }, (_, i) => (
                  <div key={i} className="score-dot"
                    style={{ background: i < filled ? color : 'var(--bg3)', transition: `background ${0.1 * i}s` }}
                  />
                ))}
              </div>
              <span className="score-val">{d.score?.toFixed(1)}</span>
              <span style={{ fontSize: 10, marginLeft: 4, padding: '1px 6px', borderRadius: 8,
                background: color + '22', color, fontWeight: 700 }}>
                {d.rec}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Main Charts component ───────────────────────────────────────────────── */
export default function Charts({ data }) {
  const hasAny = data.coverage.length || data.security.length || data.scores.length;
  if (!hasAny) return null;

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title-accent">📊</span> Live Analytics
      </div>
      <div style={{ padding: '16px 18px' }}>
        <div className="charts-row">
          <ScoreChart    data={data.scores}   />
          <CoverageChart data={data.coverage} />
          <SecurityDonut data={data.security} />
        </div>
      </div>
    </div>
  );
}
